import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { ErrorFormatterService } from '@core/services/error-formatter.service';
import { EPlatform } from '@enums/campaign.enum';
import { ComponentStore } from '@ngrx/component-store';
import { camelCase, get } from 'lodash';
import { EMPTY, MonoTypeOperatorFunction, Observable, pipe } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { IChannel, ICreatorChannelAnalyticsResponse } from '../creators.interface';
import { CreatorPlatformListService } from './creator-platform-list.service';

interface ICreatorPlatformListState {
  channels: IChannel[];
  creatorAnalytics: ICreatorChannelAnalyticsResponse[];
  loading: boolean;
  error: string | null;
}

const initialState: ICreatorPlatformListState = {
  channels: [],
  creatorAnalytics: [],
  error: null,
  loading: false,
};

@Injectable()
export class CreatorPlatformListStore extends ComponentStore<ICreatorPlatformListState> {
  public readonly channels$ = this.selectSignal((state: ICreatorPlatformListState) => state.channels);
  public readonly creatorAnalytics$ = this.selectSignal((state: ICreatorPlatformListState) => state.creatorAnalytics);
  public readonly error$ = this.selectSignal((state: ICreatorPlatformListState) => state.error);
  public readonly loading$ = this.selectSignal((state: ICreatorPlatformListState) => state.loading);

  public readonly refreshedChannel = signal<IChannel | null>(null);

  public setInitialChannels(channels: IChannel[]): void {
    this.patchState({
      channels: channels
        .map((channel) => ({ ...channel, platform: camelCase(channel.platform) as EPlatform }))
        .sort((a, b) => a.platform.localeCompare(b.platform)),
    });
  }

  private readonly handleError = (): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError((error: HttpErrorResponse) => {
        this.patchState({
          error: get(error.error, 'error_code') === ErrorFormatterService.nonVisualErrorCode ? '' : error.message,
          loading: false,
        });

        return EMPTY;
      }),
    );

  public readonly refreshChannelIcon = this.effect((channelId$: Observable<{ id: string; creatorMode?: boolean }>) => {
    return channelId$.pipe(
      tap(() => {
        this.patchState({ error: null, loading: true });
      }),
      switchMap(({ id, creatorMode }) => {
        const fetch$ = creatorMode
          ? this.service.refreshChannelIconCurrentCreator(id)
          : this.service.refreshChannelIcon(id);

        return fetch$.pipe(
          tap((channel) => {
            this.patchState({
              channels: this.get().channels.map((c) => (c.id === id ? channel : c)),
              loading: false,
            });

            this.refreshedChannel.set(channel);
          }),
          this.handleError(),
        );
      }),
    );
  });

  public readonly deleteChannel = this.effect(
    (channelId$: Observable<{ id: string; creatorMode?: boolean; onDeleted?: () => void }>) => {
      return channelId$.pipe(
        tap(() => {
          this.patchState({ error: null, loading: true });
        }),
        switchMap(({ id, creatorMode, onDeleted }) => {
          const fetch$ = creatorMode ? this.service.deleteChannelCurrentCreator(id) : this.service.deleteChannel(id);

          return fetch$.pipe(
            tap(() => {
              this.patchState({
                channels: [...this.get().channels.filter((channel) => channel.id !== id)],
                loading: false,
              });
              // Only fire once the delete actually succeeds - handleError() below leaves the
              // channel in place on failure, so tracking here would misreport a failed delete.
              onDeleted?.();
            }),
            this.handleError(),
          );
        }),
      );
    },
  );

  public readonly fetchCreatorAnalytics = this.effect(
    (
      params$: Observable<{
        creatorId?: string;
        adminMode?: boolean;
        creatorMode?: boolean;
      }>,
    ) => {
      return params$.pipe(
        tap(() => this.patchState({ error: null, loading: true })),
        switchMap(({ creatorId, adminMode, creatorMode }) => {
          const fetch$ = adminMode
            ? this.service.getAdminCreatorAnalyticsByCreatorId(creatorId!)
            : creatorMode
              ? this.service.getCreatorAnalyticsByCurrentCreator()
              : this.service.getCreatorAnalyticsByCreatorId(creatorId!);

          return fetch$.pipe(
            tap((creatorAnalytics: ICreatorChannelAnalyticsResponse[]) => {
              this.patchState({ creatorAnalytics, loading: false });
            }),
            this.handleError(),
          );
        }),
      );
    },
  );

  constructor(private readonly service: CreatorPlatformListService) {
    super(initialState);
  }
}
